import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { Modal, message, Space, Button } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DynamicForm from "@/components/DynamicForm";
import ProcessTimeline from "@/components/ProcessTimeline";
import QRCodeApprovalModal from "../components/QRCodeApprovalModal";
import type { FormInstance } from "antd";
import type { ProcessTimelineItem } from "@/components/ProcessTimeline";
import { getEvaluationFormItems } from "./config/editForm";
import {
  getAccessEvaluationById,
  startMeetingApproval,
  getHistoryByInsId,
  approvalPass,
} from "@/apis/supplier";
import aiIcon1 from "@/asserts/images/supplier/ai-icon1.png";
import aiIcon2 from "@/asserts/images/supplier/ai-icon2.png";
import styles from "../config/style.module.less";
import editStyles from "./config/edit.style.module.less";

// ==================== 类型定义 ====================
/**
 * 评估摘要 - 用于显示评估结果统计
 * poorCount: 差的数量
 * middleCount: 中的数量
 * excellentCount: 优的数量
 */
interface EvaluationSummary {
  poorCount: number;
  middleCount: number;
  excellentCount: number;
}

interface EvaluationModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: EvaluationFormData) => void;
  recordData?: any; // 编辑时的记录数据
  isViewOnly?: boolean; // 是否为查看模式
  isFromVsmc?: boolean; // 是否来自发起VSMC会议按钮
  isReassess?: boolean; // 是否为重新评估模式
  currentPersonFlag?: boolean; // 用于控制VSMC会议评审部分的显示隐藏
  isFromGm?: boolean; // 是否来自GM工作台
  isFromAdmission?: boolean; // 是否从准入申请表打开（有预加载数据）
}

interface EvaluationFormData {
  materialName: string;
  materialNumber: string;
  materialPurpose: string;
  assessmentType: string;
  assessmentDate: any;
  notes: string;
}

// ==================== 工具函数 ====================
/**
 * 从结果详情列表中计算总体评估摘要
 * 遍历 resultDetailsList 中的每一项，统计各等级的数值总和
 *
 * @param resultDetailsList - 评估结果详情列表
 * @returns 评估摘要对象，包含各等级的计数
 */
const calculateEvaluationSummary = (
  resultDetailsList: any,
): EvaluationSummary => {
  let poorCount = 0;
  let middleCount = 0;
  let excellentCount = 0;

  if (!Array.isArray(resultDetailsList)) {
    return {
      poorCount,
      middleCount,
      excellentCount,
    };
  }

  // 遍历结果详情列表，统计各等级的总数
  resultDetailsList.forEach((item: any) => {
    if (item) {
      // poorNum - 差
      if (typeof item.poorNum === "number") {
        poorCount += item.poorNum;
      }
      // middleNum - 中
      if (typeof item.middleNum === "number") {
        middleCount += item.middleNum;
      }
      // excellentNum - 优
      if (typeof item.excellentNum === "number") {
        excellentCount += item.excellentNum;
      }
    }
  });

  return {
    poorCount,
    middleCount,
    excellentCount,
  };
};

/**
 * 将后端返回的数据字段映射到表单字段名
 * 处理字段名转换，确保数据能正确回显
 *
 * @param backendData - 后端接口返回的数据
 * @returns 映射后的表单数据
 */
const transformBackendDataToFormFields = (backendData: any): any => {
  const fieldMapping: Record<string, string> = {
    // 供应商基本信息字段映射
    registrationMarketName: "businessRegistration",
    productionRegion: "productionArea",
  };

  const formData = { ...backendData };

  // 遍历映射关系，将后端字段转换为表单字段
  Object.entries(fieldMapping).forEach(([backendKey, formKey]) => {
    if (backendKey in backendData) {
      formData[formKey] = backendData[backendKey];
    }
  });

  return formData;
};

// 表单配置
const FORM_CONFIG = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  layout: "horizontal" as const,
  colon: false,
};

// ==================== 渲染函数 ====================
const getModalTitle = (processCode?: string, t?: any) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        paddingLeft: "24px",
      }}
    >
      <span>{t ? t("供应商准入评估表") : "供应商准入评估表"}</span>
      <div
        style={{
          width: "2px",
          height: "20px",
          backgroundColor: "#d9d9d9",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.45)" }}>
        {t ? t("单号") : "单号"}：{processCode || "XZ001"}
      </span>
    </div>
  );
};

const getModalFooter = (
  handleSubmit: () => void,
  handleCancelApproval: () => void,
  isFromVsmc: boolean = false,
  isApprovalInProgress: boolean = false,
  isFromGm: boolean = false,
  isReassess: boolean = false,
  t?: any,
) => {
  // 审批中状态：显示取消会议审批和提交两个按钮
  // if (isApprovalInProgress) {
  //   return (
  //     <div className={styles.modalFooterContainer}>
  //       <Space>
  //         <Button
  //           danger
  //           className={styles.cancelButton}
  //           onClick={handleCancelApproval}
  //         >
  //           取消会议审批
  //         </Button>
  //         <Button
  //           type="primary"
  //           className={styles.submitButton}
  //           onClick={handleSubmit}
  //         >
  //           提交
  //         </Button>
  //       </Space>
  //     </div>
  //   );
  // }

  // 发起VSMC会议状态：显示发起会议审批按钮
  if (isFromVsmc) {
    return (
      <div className={styles.modalFooterContainer}>
        <Space>
          <Button
            type="primary"
            className={styles.submitButton}
            onClick={handleSubmit}
          >
            {t ? t("发起会议审批") : "发起会议审批"}
          </Button>
        </Space>
      </div>
    );
  }

  // GM工作台状态：显示提交按钮
  if (isFromGm) {
    return (
      <div className={styles.modalFooterContainer}>
        <Space>
          <Button
            type="primary"
            className={styles.submitButton}
            onClick={handleSubmit}
          >
            {t ? t("提交") : "提交"}
          </Button>
        </Space>
      </div>
    );
  }

  // 重新评估状态：显示提交按钮
  if (isReassess) {
    return (
      <div className={styles.modalFooterContainer}>
        <Space>
          <Button
            type="primary"
            className={styles.submitButton}
            onClick={handleSubmit}
          >
            {t ? t("提交") : "提交"}
          </Button>
        </Space>
      </div>
    );
  }

  return null; // 其他情况不显示footer
};

const renderAIEvaluation = () => (
  <div className={editStyles.aiEvaluationContainer}>
    {/* 头部 - 标题和图标 */}
    <div className={editStyles.aiEvaluationHeader}>
      <div className={editStyles.aiEvaluationTitleWrapper}>
        <img
          src={aiIcon1}
          alt="ai-icon-left"
          className={editStyles.aiIconLeft}
        />
      </div>
      <img
        src={aiIcon2}
        alt="ai-icon-right"
        className={editStyles.aiIconRight}
      />
    </div>
    {/* 内容 - 文本 */}
    <div className={editStyles.aiEvaluationContent}>
      <div className={editStyles.aiEvaluationText}>
        根据XXXXX供应商本次准入评估结果可知，该供应商在质量维度、供应维度存在比较高的风险。评估人王小明认为，该供应商的注册资料上存在数据不匹配问题，故而希望再次注意以上问题的重视。
      </div>
    </div>
  </div>
);

// ==================== 主组件 ====================
const AssessmentModal: React.FC<EvaluationModalProps> = ({
  visible,
  onClose,
  onSubmit,
  recordData,
  isViewOnly = false,
  isFromVsmc = false,
  isReassess = false,
  currentPersonFlag = false,
  isFromGm = false,
  isFromAdmission = false,
}) => {
  const { t } = useTranslation("evaluationTab");
  const formRef = useRef<FormInstance>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>();
  const [evaluationSummary, setEvaluationSummary] =
    useState<EvaluationSummary>();
  const [timelineData, setTimelineData] = useState<ProcessTimelineItem[]>([]);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);

  // 判断是否处于审批中状态（evaluationStatus === 3）
  const isApprovalInProgress = formData?.evaluationStatus === 3;

  // ==================== 刷新签批意见 ====================
  const handleRefreshSignature = useCallback(async () => {
    if (!recordData?.id) {
      message.error("缺少评估ID，无法刷新");
      return;
    }

    try {
      setLoading(true);
      // 调用接口重新获取评估详情
      const response = await getAccessEvaluationById(recordData.id);

      if (response.data?.code === 200) {
        let responseData = response.data?.data;

        // 格式化日期字段
        if (responseData?.createTime) {
          responseData.createTime = dayjs(responseData.createTime);
        }

        // 转换后端字段到表单字段
        responseData = transformBackendDataToFormFields(responseData);

        // 更新表单数据
        setFormData(responseData);
        formRef.current?.setFieldsValue(responseData);

        message.success("签批意见已刷新");
      } else {
        message.error(response.data?.message || "刷新失败");
      }
    } catch (error) {
      message.error("刷新签批意见失败，请重试");
    } finally {
      setLoading(false);
    }
  }, [recordData?.id]);

  const formItems = useMemo(() => {
    return getEvaluationFormItems(
      t,
      isViewOnly,
      formData,
      evaluationSummary,
      currentPersonFlag,
      () => setQrCodeModalVisible(true),
      isApprovalInProgress,
      isFromVsmc,
      isFromGm, // 使用 prop 而不是 formData?.isFromGm
      handleRefreshSignature,
    );
  }, [
    t,
    isViewOnly,
    formData,
    evaluationSummary,
    currentPersonFlag,
    isApprovalInProgress,
    isFromVsmc,
    isFromGm,
    handleRefreshSignature,
  ]);
  const modalTitle = useMemo(
    () => getModalTitle(formData?.processCode, t),
    [formData?.processCode, t],
  );

  // 当弹框打开或recordData改变时，获取详情数据并初始化表单
  useEffect(() => {
    if (visible && recordData?.id) {
      // 检查是否已经有预加载的评估数据（从准入申请表打开时传递）
      // 只有当有 resultDetailsList 时才认为数据已预加载，无需再次调用接口
      // 注意：仅有 evaluationStatus 不足以判断数据完整性，因为表格行数据也包含此字段
      const hasPreloadedData =
        recordData?.resultDetailsList &&
        Array.isArray(recordData.resultDetailsList) &&
        recordData.resultDetailsList.length > 0;

      if (hasPreloadedData || isFromAdmission) {
        // 使用预加载的数据，无需调用接口
        let data = { ...recordData };
        if (data?.createTime) {
          data.createTime = dayjs(data.createTime);
        }
        // 转换后端字段到表单字段
        data = transformBackendDataToFormFields(data);
        // 从结果详情列表中计算摘要
        if (data?.resultDetailsList) {
          const summary = calculateEvaluationSummary(data.resultDetailsList);
          setEvaluationSummary(summary);
        } else {
          setEvaluationSummary(undefined);
        }
        // 设置整个 formData
        setFormData(data);
        formRef.current?.setFieldsValue(data);
        return;
      }

      setLoading(true);
      // 调用接口获取评估详情
      getAccessEvaluationById(recordData.id)
        .then((response) => {
          if (response.data?.code === 200) {
            let responseData = response.data?.data;

            // 获取历史记录（如果有insId）
            if (responseData?.insId) {
              getHistoryByInsId(responseData.insId)
                .then((historyResponse) => {
                  // 将历史记录转换为 ProcessTimelineItem 格式
                  if (
                    historyResponse?.data?.code === 200 &&
                    Array.isArray(historyResponse?.data?.data)
                  ) {
                    const timeline = historyResponse.data.data.map(
                      (item: any) => ({
                        approver: item.approver,
                        flowStatus: item.flowStatus,
                        postName: item.postName,
                        createTime: item.createTime,
                        message: item.message,
                        avatarUrl:
                          item.avatarUrl ||
                          "https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg",
                      }),
                    );
                    setTimelineData(timeline);
                  }
                })
                .catch((historyError) => {
                  console.error("获取历史记录失败:", historyError);
                });
            }

            // 格式化日期字段，只取年月日部分
            if (responseData?.createTime) {
              responseData.createTime = dayjs(responseData.createTime);
            }
            // 转换后端字段到表单字段
            responseData = transformBackendDataToFormFields(responseData);
            // 从结果详情列表中计算摘要
            const summary = calculateEvaluationSummary(
              responseData.resultDetailsList,
            );
            setEvaluationSummary(summary);
            // 设置整个 formData
            setFormData(responseData);

            formRef.current?.setFieldsValue(responseData);
          } else {
            message.error(response.data?.message || "获取评估详情失败");
          }
        })
        .catch((error) => {
          message.error("获取评估详情失败，请重试");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (visible && recordData) {
      // 如果没有 id，直接使用 recordData，但需要格式化日期
      let data = { ...recordData };
      if (data?.createTime) {
        data.createTime = dayjs(data.createTime);
      }
      // 转换后端字段到表单字段
      data = transformBackendDataToFormFields(data);
      // 从结果详情列表中计算摘要
      if (data?.resultDetailsList) {
        const summary = calculateEvaluationSummary(data.resultDetailsList);
        setEvaluationSummary(summary);
      } else {
        setEvaluationSummary(undefined);
      }
      // 设置整个 formData
      setFormData(data);
      formRef.current?.setFieldsValue(data);
    } else if (!visible) {
      setEvaluationSummary(undefined);
      setFormData(undefined);
      setTimelineData([]);
      formRef.current?.resetFields();
    }
  }, [visible, recordData, isFromAdmission]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await formRef.current?.validateFields();
      if (values) {
        // 如果来自发起会议审批按钮，则调用 /view/start 接口
        if (isFromVsmc) {
          const businessId = formData?.id;
          const supplierUserId = formData?.supplierUserId;
          const formNo = formData?.processCode || ""; // 流程编号
          const reviewComments = values.reviewComments || ""; // 意见说明
          const reviewOpinion = values.reviewOpinion; // 审核意见

          if (!businessId) {
            message.error("缺少业务ID，无法发起会议审批");
            return;
          }

          if (!supplierUserId) {
            message.error("缺少供应商ID，无法发起会议审批");
            return;
          }

          // 根据审核意见设置参数
          let toGm = 0;
          let skipType = "";
          if (reviewOpinion === "toGm") {
            toGm = 1;
            skipType = "PASS";
          } else if (reviewOpinion === "agree") {
            toGm = 0;
            skipType = "PASS";
          } else if (reviewOpinion === "reject") {
            toGm = 0;
            skipType = "";
          }

          try {
            // 调用发起会议审批接口
            const response = await startMeetingApproval({
              flowCode: "ZHUN_RU_PING_GU",
              message: reviewComments,
              skipType,
              variable: {
                businessId,
                supplierUserId,
                toGm,
              },
              formNo,
            });

            if (response?.data?.code === 200) {
              const successMessage =
                reviewOpinion === "agree"
                  ? "已成功通过审批"
                  : reviewOpinion === "toGm"
                    ? "已提交至GM决策"
                    : "已驳回";
              message.success(successMessage);
              formRef.current?.resetFields();
              onClose();
            } else {
              message.error(response?.data?.message || "发起会议审批失败");
            }
          } catch (apiError) {
            message.error("发起会议审批接口调用失败，请重试");
          }
        } else if (isFromGm && values.reviewOpinion) {
          // GM审批状态：调用 /view/pass 接口进行审批
          const taskId = formData?.taskId;
          const businessId = formData?.id;
          const reviewOpinion = values.reviewOpinion;
          const reviewComments = values.reviewComments || "";

          if (!taskId) {
            message.error("缺少任务ID，无法进行审批");
            return;
          }

          if (!businessId) {
            message.error("缺少业务ID，无法进行审批");
            return;
          }

          // 构建审批参数
          const approvalParams: any = {
            message: reviewComments,
            flowCode: "ZHUN_RU_PING_GU",
            variable: {
              businessId,
            },
          };

          // 根据审核意见设置参数
          if (reviewOpinion === "agree") {
            // GM通过
            approvalParams.skipType = "PASS";
            approvalParams.variable.evaluationStatus = 5;
          } else if (reviewOpinion === "reject") {
            // GM驳回：不传skipType字段
            approvalParams.variable.evaluationStatus = 4;
          }

          try {
            // 调用审批接口
            const response = await approvalPass(approvalParams, taskId);

            if (response?.data?.code === 200) {
              const successMessage =
                reviewOpinion === "agree" ? "GM审批已通过" : "GM审批已驳回";
              message.success(successMessage);
              formRef.current?.resetFields();
              onClose();
            } else {
              message.error(response?.data?.message || "GM审批失败");
            }
          } catch (apiError) {
            message.error("GM审批接口调用失败，请重试");
          }
        } else if (isApprovalInProgress && values.reviewOpinion) {
          // 审批中状态：调用 /view/pass 接口进行审批
          const taskId = formData?.taskId;
          const reviewOpinion = values.reviewOpinion;
          const reviewComments = values.reviewComments || "";

          if (!taskId) {
            message.error("缺少任务ID，无法进行审批");
            return;
          }

          // 构建审批参数
          const approvalParams: any = {
            message: reviewComments,
            flowCode: "ZHUN_RU_PING_GU",
            supplierUserId: formData.supplierUserId,
            variable: {},
          };

          // 根据审核意见设置参数
          if (reviewOpinion === "agree") {
            // 同意
            approvalParams.skipType = "PASS";
            approvalParams.variable.toGm = 0;
          } else if (reviewOpinion === "reject") {
            // 驳回：不传skipType
            approvalParams.variable.toGm = 0;
          } else if (reviewOpinion === "toGm") {
            // 需呈GM决策
            approvalParams.skipType = "PASS";
            approvalParams.variable.toGm = 1;
          }

          try {
            // 调用审批接口
            const response = await approvalPass(approvalParams, taskId);

            if (response?.data?.code === 200) {
              const successMessage =
                reviewOpinion === "agree"
                  ? "已成功通过审批"
                  : reviewOpinion === "toGm"
                    ? "已提交至GM决策"
                    : "已驳回";
              message.success(successMessage);
              formRef.current?.resetFields();
              onClose();
            } else {
              message.error(response?.data?.message || "审批失败");
            }
          } catch (apiError) {
            message.error("审批接口调用失败，请重试");
          }
        } else if (isReassess) {
          // 重新评估状态：调用 /view/start 接口
          const businessId = formData?.id;
          const supplierUserId = formData?.id;
          const formNo = formData?.processCode || ""; // 流程编号
          const reviewComments = values.reviewComments || ""; // 意见说明

          if (!businessId) {
            message.error("缺少业务ID，无法提交重新评估");
            return;
          }

          if (!supplierUserId) {
            message.error("缺少供应商ID，无法提交重新评估");
            return;
          }

          try {
            // 调用重新评估接口
            const response = await startMeetingApproval({
              flowCode: "ZHUN_RU_PING_GU",
              message: reviewComments,
              variable: {
                businessId,
                supplierUserId,
              },
              formNo,
            });

            if (response?.data?.code === 200) {
              message.success("重新评估已提交");
              formRef.current?.resetFields();
              onClose();
            } else {
              message.error(response?.data?.message || "重新评估提交失败");
            }
          } catch (apiError) {
            message.error("重新评估接口调用失败，请重试");
          }
        } else {
          // 否则执行原有的提交逻辑
          onSubmit(values);
          message.success("评估表已提交");
          formRef.current?.resetFields();
          onClose();
        }
      }
    } catch (error) {
      // 表单验证失败
      // validateFields 失败时不需要显示通用错误提示，Ant Design 会自动显示字段错误
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    formRef.current?.resetFields();
    onClose();
  };

  const handleCancelApproval = async () => {
    try {
      setLoading(true);
      // TODO: 调用取消会议审批的接口
      // 需要根据实际业务需求实现接口调用逻辑
      message.success("已取消会议审批");
      formRef.current?.resetFields();
      onClose();
    } catch (error) {
      message.error("取消会议审批失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        confirmLoading={loading}
        width="1400px"
        centered
        style={{ top: 10, paddingBottom: 20 }}
        classNames={{
          header: styles.modalHeader,
          body: styles.modalBody,
        }}
        footer={() =>
          getModalFooter(
            handleSubmit,
            handleCancelApproval,
            isFromVsmc,
            isApprovalInProgress,
            isFromGm,
            isReassess,
            t,
          )
        }
        onCancel={handleCancel}
      >
        <div style={{ display: "flex", gap: "20px" }}>
          {/* 左侧表单区域 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DynamicForm
              ref={formRef}
              formItem={formItems}
              formConfig={FORM_CONFIG}
              readOnly={isViewOnly}
              isViewMode={isViewOnly}
            />
          </div>

          {/* 右侧流程动态区域 - 当 evaluationStatus 为 3/4/5 且有流程数据时显示 */}
          {(formData?.evaluationStatus === 3 ||
            formData?.evaluationStatus === 4 ||
            formData?.evaluationStatus === 5) &&
            timelineData.length > 0 && (
              <div style={{ width: "320px", flexShrink: 0 }}>
                <div
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1d2129",
                  }}
                >
                  {t("流程动态")}
                </div>
                <ProcessTimeline items={timelineData} />
              </div>
            )}
        </div>
      </Modal>
      <QRCodeApprovalModal
        visible={qrCodeModalVisible}
        onClose={() => setQrCodeModalVisible(false)}
        processCode={formData?.processCode}
        businessId={formData?.id}
      />
    </>
  );
};

export { renderAIEvaluation };
export default AssessmentModal;
